import { Controller } from "@hotwired/stimulus"

/**
 * This controller is used to activate/deactivate tabs and scroll to the corresponding section.
 *
 * Usage:
 * Add these attributes to the container element:
 *   data-controller="anchor-scroll-navigation"
 *   data-anchor-scroll-navigation-inside-modal-value="true/false"
 *   data-anchor-scroll-navigation-scroll-value="true/false" (optional, default is true)
 *
 * Add these attributes to tab buttons:
 *   data-anchor="anchor_name"
 *   data-anchor-scroll-navigation-target="anchorScrollButton"
 *   data-action="click->anchor-scroll-navigation#scrollToSection"
 *
 * Add these attributes to tab contents:
 *   id="anchor_name"
 *   data-anchor-scroll-navigation-target="anchorScrollSection"
 */

export default class extends Controller {
  static targets = ["anchorScrollButton", "anchorScrollSection"]
  static values = {
    insideModal: Boolean,
    scroll: { type: Boolean, default: true }
  }

  connect() {
    this.activateFirstButton()
    this.initInteractionObserver()
    this.bindButtonClickHandlers()

    // Mark lazy turbo-frames as loaded once their content arrives so we never
    // re-trigger them (which would wipe any in-progress UI inside). Mirrors
    // tabs_controller#_markFrameLoaded.
    this.boundMarkFrameLoaded = this._markFrameLoaded.bind(this)
    this.element.addEventListener("turbo:frame-load", this.boundMarkFrameLoaded)
  }

  disconnect() {
    this.observer.disconnect()
    this.element.removeEventListener("turbo:frame-load", this.boundMarkFrameLoaded)
  }

  initInteractionObserver() {
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      rootMargin: "-20% 0% -80% 0%",
      threshold: [0]
    })
    this.anchorScrollSectionTargets.forEach(section => this.observer.observe(section))
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.activateButtonForSection(entry.target.id)
      }
    })
  }

  activateButtonForSection(sectionId) {
    this.anchorScrollButtonTargets.forEach(button => {
      const buttonSectionId = button.dataset.anchor
      button.classList.toggle("active", buttonSectionId === sectionId)
    })
  }

  activateFirstButton() {
    if (this.hasAnchorScrollButtonTarget) {
      this.anchorScrollButtonTarget.classList.add("active")
    }
  }

  bindButtonClickHandlers() {
    this.anchorScrollButtonTargets.forEach(button => {
      button.addEventListener('click', this.scrollToSection.bind(this))
    })
  }

  scrollToSection(event) {
    event.preventDefault()
    const target = event.target
    const sectionId = target.dataset.anchor
    const section = this.anchorScrollSectionTargets.find(({ id }) => id === sectionId)
    if (!section) return

    this._triggerLazyFrames(section)

    if (this.insideModalValue) {
      const options = { block: "start" }
      if (this.scrollValue) options.behavior = "smooth"
      section.scrollIntoView(options)
      return
    }

    const scrollMarginTop = parseInt(getComputedStyle(section).scrollMarginTop, 10) || 0
    const scrollPaddingTop = parseInt(getComputedStyle(section).scrollPaddingTop, 10) || 0
    const offset = scrollMarginTop + scrollPaddingTop

    if (this.scrollValue) {
      window.scrollTo({
        top: section.getBoundingClientRect().top + window.scrollY - offset,
        behavior: "smooth"
      })
    } else {
      section.scrollIntoView({ block: "start" })
    }
  }

  // Kick lazy turbo-frames inside the targeted section so the user doesn't have
  // to wait for the smooth-scroll IntersectionObserver to fire. Mirrors the
  // idempotency pattern in tabs_controller: skip frames that have already
  // loaded (re-assigning src would wipe their content).
  _triggerLazyFrames(section) {
    section.querySelectorAll('turbo-frame[loading="lazy"][src]').forEach(frame => {
      if (frame.dataset.lazyLoaded === "true") return
      if (!frame.complete) frame.src = frame.src
    })
  }

  _markFrameLoaded(event) {
    const frame = event.target
    if (frame.tagName === "TURBO-FRAME") {
      frame.dataset.lazyLoaded = "true"
    }
  }
}
